import type { Branch } from "@domain/CRM/branch";
import type { Company } from "@domain/CRM/company";
import type { CompanySettings } from "@domain/Settings";
import type { LogData } from "@rogoag/shared-ts";
import type { IBranches } from "@ports/branches/IBranches";
import type { ICompanies } from "@ports/companies/ICompanies";
import type { ILogger } from "@ports/logger/ILogger";

export interface ImportSettingsUseCasesDeps {
	companiesApi: ICompanies;
	branchesApi: IBranches;
	logger: ILogger;
}

/** Visibility/required pair for a single import-table column — the shape
 *  the use case persists into `Company.portalSettings.importTableColumns`. */
export interface ImportColumnSetting {
	visible: boolean;
	required: boolean;
}

export interface SaveImportSettingsInput {
	company: Company;
	columnSettings: Record<string, ImportColumnSetting>;
	importFlowSettings: Record<string, boolean>;
	/** The set of `portalConfigurationOptions` keys that this UI manages.
	 *  Any key in `company.portalConfigurationOptions` that is NOT in this set
	 *  is *preserved* during the merge — that's how the page avoids clobbering
	 *  unrelated flags (e.g. `Submission: ...` options) it doesn't render. */
	importFlowOptionKeys: readonly string[];
	/** Company-level Agrian org IDs, replacing whatever is stored. */
	companyAgrianOrgIds: string[];
	/** Branch id → its Agrian org IDs, replacing whatever is stored. */
	branchAgrianOrgIds: Record<string, string[]>;
	/** The branches currently loaded for the company — the change-gate compares
	 *  against these so unchanged branches are not written. */
	branches: Branch[];
	/** Agrian: import only from pre-existing sample events (see `AgrianCompanySettings`).
	 *  Persisted into `portalSettings.agrian`, alongside any sibling flag added later. */
	preExistingEventsOnly: boolean;
}

/**
 * Drives the Admin → ImportSettings page.
 *
 *  - `loadCompanies()` — fetches every active company; the page renders a
 *    selector populated from this list.
 *
 *  - `save(input)` — orchestrates two backend updates plus a critical merge:
 *      1. `updatePortalSettings(company.id, { ...existing, importTableColumns })`
 *         — persists the column visibility/required map.
 *      2. `updatePortalConfigurationOptions(company.id, mergedOptions)`
 *         — when `importFlowSettings` differs from the existing flags. The
 *         merged list is `[enabled-from-flow, ...preserved-non-import]`:
 *         enabled keys come from `importFlowSettings`; everything in the
 *         current `portalConfigurationOptions` that is NOT in
 *         `importFlowOptionKeys` is appended unchanged so unrelated flags
 *         survive.
 *      Returns the updated Company object the page should put in state.
 *
 * The merge invariant — "this UI only owns the keys it actually renders" —
 * is what the bd ticket flagged as untested before extraction. The unit
 * tests below pin every merge case (no flow change, partial enable,
 * non-import preservation, full disable).
 */
export interface ImportSettingsUseCases {
	/** The optional `fresh` flag bypasses the server-side read cache while still
	 *  repopulating it — see `useFreshOnEntry`. */
	loadCompanies(fresh?: boolean): Promise<Company[]>;
	/** Fetches the company's branches (name-sorted) so the page can render one
	 *  Agrian org picker per branch. No `fresh` bypass here — not because there's no
	 *  entry point to hang one off (a `scopeKey`'d floor on the selected company's id
	 *  would work exactly like the other three fresh-on-entry pages'), but because
	 *  `IBranches` doesn't carry the flag; extending it was out of scope for this
	 *  change. `loadCompanies` above is what actually refreshes on this page's entry. */
	loadBranches(company: Company): Promise<Branch[]>;
	save(input: SaveImportSettingsInput): Promise<Company>;
}

export async function loadCompanies(deps: ImportSettingsUseCasesDeps, fresh?: boolean): Promise<Company[]> {
	const fetched = await deps.companiesApi.getByFilter(
		{
			type: "OR",
			fields: [{ isActive: 1 }],
		} as never,
		fresh,
	);
	// Airtable returns these in record order, which is arbitrary to a reader — the
	// page renders them straight into the company pickers, so sort here (same as
	// loadBranches below) rather than leaving every consumer to re-derive it.
	return fetched.slice().sort((a, b) => a.name.localeCompare(b.name));
}

export async function loadBranches(deps: ImportSettingsUseCasesDeps, company: Company): Promise<Branch[]> {
	const branchIds = company.branchIds ?? [];
	if (branchIds.length === 0) {
		return [];
	}
	const fetched = await deps.branchesApi.getByIds(branchIds);
	return fetched.slice().sort((a, b) => a.name.localeCompare(b.name));
}

/** Order-insensitive set equality — a reorder is not a change, mirroring the
 *  `optionsChanged` gate below. Exported so the VM's `hasChanges` dirty-check
 *  uses the same "did this list actually change" definition as the write-gate
 *  below, instead of re-deriving its own. */
export function sameIds(a: readonly string[], b: readonly string[]): boolean {
	const aSet = new Set(a);
	const bSet = new Set(b);
	return aSet.size === bSet.size && [...bSet].every((id) => aSet.has(id));
}

export async function save(deps: ImportSettingsUseCasesDeps, input: SaveImportSettingsInput): Promise<Company> {
	const logData: LogData = {
		function: "importSettings.save",
		airtable_record_id: input.company.id,
		airtable_table: "Companies",
	};
	deps.logger.log("Saving import settings", logData);

	const updatedSettings = {
		...(input.company.portalSettings || { views: [] }),
		importTableColumns: input.columnSettings as CompanySettings["importTableColumns"],
		// Spread the stored `agrian` object so a sibling flag added later survives a save from
		// this page — the same "only own the keys you render" invariant the
		// portalConfigurationOptions merge below enforces.
		agrian: {
			...(input.company.portalSettings?.agrian ?? {}),
			preExistingEventsOnly: input.preExistingEventsOnly,
		},
	} as CompanySettings;

	await deps.companiesApi.updatePortalSettings(input.company.id, updatedSettings);

	const mergedOptions = mergePortalConfigurationOptions(
		input.company.portalConfigurationOptions ?? [],
		input.importFlowSettings,
		input.importFlowOptionKeys,
	);

	// Only round-trip the configuration-options endpoint when the merged
	// list actually differs from what the company already has. The vm
	// previously gated on a JSON.stringify equality of the per-key map;
	// the array-equality check here is equivalent for the keys this UI
	// owns and avoids a no-op POST in the common save-with-no-flow-change
	// case.
	const existing = (input.company.portalConfigurationOptions ?? []) as readonly string[];
	const existingSet = new Set(existing);
	const mergedSet = new Set(mergedOptions);
	const optionsChanged = existingSet.size !== mergedSet.size || mergedOptions.some((opt) => !existingSet.has(opt));
	if (optionsChanged) {
		await deps.companiesApi.updatePortalConfigurationOptions(input.company.id, mergedOptions);
	}

	// Agrian org IDs — company first, then each *changed* branch, sequentially.
	//
	// Partial-failure semantics (deliberate, do not "fix" this into a
	// transaction — Airtable cannot give us one): a failure here throws and
	// propagates to the vm's Oops.handleError, leaving earlier writes applied.
	// That is safe because (1) every write is an idempotent *replace*, not a
	// delta, so retrying converges on the intended state, and (2) the vm only
	// refreshes its `original*` mirrors after save() resolves, so a throw
	// leaves the page dirty and the user simply retries the whole set.
	// No retry/rollback machinery.
	if (!sameIds(input.company.externalLocationId ?? [], input.companyAgrianOrgIds)) {
		await deps.companiesApi.updateAgrianOrganizationIds(input.company.id, input.companyAgrianOrgIds);
	}

	for (const branch of input.branches) {
		const selected = input.branchAgrianOrgIds[branch.id];
		if (selected === undefined) continue;
		if (sameIds(branch.externalLocationId ?? [], selected)) continue;
		await deps.branchesApi.updateAgrianOrganizationIds(branch.id, selected);
	}

	deps.logger.log("Import settings saved successfully", logData);
	return {
		...input.company,
		portalSettings: updatedSettings,
		portalConfigurationOptions: mergedOptions as Company["portalConfigurationOptions"],
		externalLocationId: input.companyAgrianOrgIds,
	};
}

export function makeImportSettingsUseCases(deps: ImportSettingsUseCasesDeps): ImportSettingsUseCases {
	return {
		loadCompanies: (fresh) => loadCompanies(deps, fresh),
		loadBranches: (company) => loadBranches(deps, company),
		save: (input) => save(deps, input),
	};
}

/**
 * Pure merge: keys in `importFlowSettings` that are `true` come first
 * (in insertion order), followed by every entry in `existing` whose key
 * is NOT in `importFlowOptionKeys`. The non-import keys preserve their
 * relative order from the original list.
 */
export function mergePortalConfigurationOptions(
	existing: readonly string[],
	importFlowSettings: Record<string, boolean>,
	importFlowOptionKeys: readonly string[],
): string[] {
	const enabled = Object.entries(importFlowSettings)
		.filter(([, v]) => v)
		.map(([k]) => k);
	const importKeys = new Set(importFlowOptionKeys);
	const preserved = existing.filter((opt) => !importKeys.has(opt));
	return [...enabled, ...preserved];
}
